import { RepliersListing } from '../../types/repliers/RepliersListing';
import { DateTime } from 'luxon';
import { DATE_YYYY_MM_DD } from '../../constants/dateTimes';
import { TORONTO_BOARD_RESOURCE_CODE } from '../../constants/realEstateBoards';

const SINGLE_NUMBER_REGEX = /^\d+$/;
const SINGLE_DECIMAL_NUMBER_REGEX = /^(\d+\.\d+)$/;
const TWO_NUMBERS_RANGE_REGEX = /^(\d+)-(\d+)$/;
const SINGLE_NUMBER_PLUS_REGEX = /^(\d+)\+$/;
const LESS_THAN_SINGLE_NUMBER_REGEX = /^<\s*(\d+)$/;
const SINGLE_NUMBER_METERS_SQUARED_REGEX = /^(\d+)\s*m2$/i;
const SINGLE_DECIMAL_NUMBER_METERS_SQUARED_REGEX = /^(\d+\.\d+)\s*m2$/i;

const SQFT_PER_METER_SQUARED = 10.7639;

const MAX_SQFT = 1_000_000;

export default function getSqftRangeFromRepliersListing(
  repliersListing: RepliersListing,
): string | null {
  const sqft = repliersListing.details.sqft;

  if (!sqft) return null;

  const sanitizedSqft = sqft
    .replace(/sqft/gi, '') // Remove 'sqft' (case-insensitive)
    .trim(); // Remove any leading or trailing spaces

  let lowerBound;
  let upperBound;

  if (sanitizedSqft.match(SINGLE_NUMBER_REGEX)) {
    // Handle cases like 450
    const sqftNumber = Number(sanitizedSqft);
    lowerBound = sqftNumber;
    upperBound = sqftNumber;
  } else if (sanitizedSqft.match(SINGLE_DECIMAL_NUMBER_REGEX)) {
    // Handle cases like 450.0000 or 451.51
    const sqftNumber = Math.round(Number(sanitizedSqft));
    lowerBound = sqftNumber;
    upperBound = sqftNumber;
  } else if (sanitizedSqft.match(TWO_NUMBERS_RANGE_REGEX)) {
    // Handle cases like 0-499, 500-699, etc.
    const twoNumbersRangeMatch = sanitizedSqft.match(TWO_NUMBERS_RANGE_REGEX);
    lowerBound = Number(twoNumbersRangeMatch[1]);
    upperBound = Number(twoNumbersRangeMatch[2]);

    // Return null if the range is invalid, like 699-500
    if (lowerBound > upperBound) return null;
  } else if (sanitizedSqft.match(SINGLE_NUMBER_PLUS_REGEX)) {
    // Handle cases like 5000+
    const singleNumberPlusMatch = sanitizedSqft.match(SINGLE_NUMBER_PLUS_REGEX);
    lowerBound = Number(singleNumberPlusMatch[1]);
    upperBound = MAX_SQFT; // We choose an arbitrarily high value to make SQL comparisons easier
  } else if (sanitizedSqft.match(LESS_THAN_SINGLE_NUMBER_REGEX)) {
    // Handle cases like < 700
    const lessThanSingleNumberMatch = sanitizedSqft.match(
      LESS_THAN_SINGLE_NUMBER_REGEX,
    );
    lowerBound = 0;
    upperBound = Number(lessThanSingleNumberMatch[1]);
  } else if (sanitizedSqft.match(SINGLE_NUMBER_METERS_SQUARED_REGEX)) {
    // Handle cases like 54 m2
    const singleNumberMetersSquaredMatch = sanitizedSqft.match(
      SINGLE_NUMBER_METERS_SQUARED_REGEX,
    );
    const metresSquaredNumber = Number(singleNumberMetersSquaredMatch[1]);
    const sqftNumber = Math.round(metresSquaredNumber * SQFT_PER_METER_SQUARED);
    lowerBound = sqftNumber;
    upperBound = sqftNumber;
  } else if (sanitizedSqft.match(SINGLE_DECIMAL_NUMBER_METERS_SQUARED_REGEX)) {
    // Handle cases like 194.58 m2
    const singleDecimalNumberMetersSquaredMatch = sanitizedSqft.match(
      SINGLE_DECIMAL_NUMBER_METERS_SQUARED_REGEX,
    );
    const metresSquaredNumber = Number(
      singleDecimalNumberMetersSquaredMatch[1],
    );
    const sqftNumber = Math.round(metresSquaredNumber * SQFT_PER_METER_SQUARED);
    lowerBound = sqftNumber;
    upperBound = sqftNumber;
  } else {
    return null;
  }

  return `[${lowerBound},${upperBound}]`;
}

/*
// REFERENCE

TREB sqft values:

NULL
0-499
500-699
5000+
< 700


ITSO sqft examples:

NULL
1420
577.69


CREA sqft examples:

NULL
1850 sqft
1085.0000
54 m2
54.35 m2
2 ac // Extremely rare - seems like an error

 */
